import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SectionHeader from "../SectionHeader";
import { useRingLifestyleData } from "./deta-LifestyleUseCases/ringData";
import { LifestyleUseCasesProps, UseCaseProps } from "./deta-LifestyleUseCases/types";
import { Clock, Sparkles, ChevronDown, CheckCircle2 } from "lucide-react";

// Import translations
import lifestyleCommonFa from "./locales/LifestyleCommon-fa.json";
import lifestyleCommonEn from "./locales/LifestyleCommon-en.json";
import lifestyleCommonAr from "./locales/LifestyleCommon-ar.json";
import lifestyleCommonTr from "./locales/LifestyleCommon-tr.json";

const UseCase: React.FC<UseCaseProps> = ({ image, title, description, time, index, color, scrollProgress, main, tags, totalCards }) => {
    const { i18n } = useTranslation();
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const getTimeBadgeColor = (timeText: string): string => {
        const morningKeywords = ["صبح", "AM", "Morning", "صباح", "Sabah"];
        const noonKeywords = ["ظهر", "PM", "Noon", "ظهر", "Öğle"];
        const afternoonKeywords = ["عصر", "Afternoon", "عصر", "Öğleden Sonra"];
        const nightKeywords = ["شب", "Night", "ليل", "Gece"];

        if (morningKeywords.some((kw) => timeText.includes(kw))) {
            return "from-sky-500 to-blue-600";
        }
        if (noonKeywords.some((kw) => timeText.includes(kw))) {
            return "from-amber-500 to-orange-600";
        }
        if (afternoonKeywords.some((kw) => timeText.includes(kw))) {
            return "from-blue-600 to-indigo-600";
        }
        if (nightKeywords.some((kw) => timeText.includes(kw))) {
            return "from-indigo-600 to-purple-700";
        }
        return "from-slate-600 to-slate-800";
    };

    const formattedIndex = (index + 1).toString().padStart(2, "0");

    return (
        <div
            ref={cardRef}
            className="mb-8 sm:mb-12 transition-all duration-300"
            dir={isRTL ? "rtl" : "ltr"}
            style={{
                position: "sticky",
                top: `${88 + index * 20}px`,
                zIndex: index + 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "1.25rem",
            }}
        >
            <div
                className="bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200/90 shadow-lg shadow-slate-200/60 hover:shadow-2xl hover:border-blue-300/80 transition-all duration-300 overflow-hidden relative group flex flex-col md:flex-row-reverse w-full"
                style={{
                    maxWidth: "840px",
                    width: "96%",
                    margin: "0 auto",
                    minHeight: "220px",
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Image Section */}
                <div className="w-full md:w-2/5 relative h-52 md:h-auto overflow-hidden">
                    <img
                        src={image}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-108"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-slate-900/15 to-transparent" />

                    {/* Scenario Index Counter Badge */}
                    <div className={`absolute bottom-3 ${isRTL ? "right-3" : "left-3"} bg-black/40 backdrop-blur-md text-white/90 text-xs font-mono font-bold px-2.5 py-1 rounded-lg border border-white/20 flex items-center gap-1`}>
                        <Sparkles className="w-3 h-3 text-amber-300" />
                        <span>{isRTL ? `سناریو ${formattedIndex}` : `Scenario #${formattedIndex}`}</span>
                    </div>

                    {/* Time Badge */}
                    <div
                        className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} bg-gradient-to-r ${getTimeBadgeColor(time)} backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/20 flex items-center gap-1.5 shadow-md`}
                    >
                        <Clock className="w-3.5 h-3.5" />
                        <span>{time}</span>
                    </div>
                </div>

                {/* Content Section */}
                <div className="w-full md:w-3/5 text-start p-5 sm:p-6 relative z-10 flex flex-col justify-between bg-gradient-to-br from-white via-white to-blue-50/20">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="h-2 w-2 rounded-full bg-blue-600 animate-pulse" />
                            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                                {isRTL ? "کاربرد هوشمند" : "Smart Life Case"}
                            </span>
                        </div>

                        <h3 className="text-lg sm:text-xl font-bold text-slate-900 transition-colors duration-200 group-hover:text-[#0038A8] leading-snug">
                            {title}
                        </h3>

                        <p className="text-slate-600 leading-relaxed text-sm mt-2.5">
                            {description}
                        </p>
                    </div>

                    {/* Tags */}
                    {Array.isArray(tags) && tags.length > 0 && (
                        <div className="pt-4 flex flex-wrap gap-1.5">
                            {tags.map((tag, i) => (
                                <span
                                    key={i}
                                    className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full border border-blue-200/70 text-[#0038A8] bg-blue-50/80 hover:bg-blue-100/80 transition-colors shadow-2xs"
                                >
                                    <CheckCircle2 className="w-3 h-3 text-blue-600" />
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Subtle Accent Bottom Line */}
                <div
                    className={`absolute bottom-0 ${isRTL ? "right-0" : "left-0"} h-1 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-sky-400 transition-all duration-300 ease-out`}
                    style={{
                        width: isHovered ? "100%" : "0%",
                    }}
                />
            </div>
        </div>
    );
};

const LifestyleUseCases: React.FC<LifestyleUseCasesProps> = ({ data, sectionTitle, className = "" }) => {
    const { t, i18n } = useTranslation("lifestyleCommon");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [activeTab, setActiveTab] = useState<string>("main");
    const [scrollProgress, setScrollProgress] = useState(0);
    const [showAllCards, setShowAllCards] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Load translations
    useEffect(() => {
        const ns = "lifestyleCommon";
        const resources = {
            fa: lifestyleCommonFa,
            en: lifestyleCommonEn,
            ar: lifestyleCommonAr,
            tr: lifestyleCommonTr,
        };

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const ringLifestyleData = useRingLifestyleData();
    const usedData = data || ringLifestyleData;

    const defaultSectionTitle = {
        regularText: t("defaultSection.regularText"),
        highlightedText: t("defaultSection.highlightedText"),
        descriptionHighlight: t("defaultSection.descriptionHighlight"),
        descriptionText: t("defaultSection.descriptionText"),
    };

    const finalSectionTitle = sectionTitle || defaultSectionTitle;

    const getTimeOrder = (time: string): number => {
        const morningKeywords = ["صبح", "AM", "Morning", "صباح", "Sabah"];
        const noonKeywords = ["ظهر", "PM", "Noon", "ظهر", "Öğle"];
        const afternoonKeywords = ["عصر", "Afternoon", "عصر", "Öğleden Sonra"];
        const nightKeywords = ["شب", "Night", "ليل", "Gece"];

        if (morningKeywords.some((kw) => time.includes(kw))) return 1;
        if (noonKeywords.some((kw) => time.includes(kw))) return 2;
        if (afternoonKeywords.some((kw) => time.includes(kw))) return 3;
        if (nightKeywords.some((kw) => time.includes(kw))) return 4;
        return 5;
    };

    const rawUseCases = (() => {
        if (activeTab === "main") {
            const list = usedData.useCases.filter((useCase) => useCase.main === true);
            return list.sort((a, b) => getTimeOrder(a.time) - getTimeOrder(b.time));
        } else if (activeTab === "all") {
            return usedData.useCases;
        } else {
            return usedData.useCases.filter((useCase) => useCase.category === activeTab);
        }
    })();

    const DEFAULT_VISIBLE_COUNT = 3;
    const totalCards = rawUseCases.length;
    const hasMore = totalCards > DEFAULT_VISIBLE_COUNT;

    const filteredUseCases = showAllCards || !hasMore
        ? rawUseCases
        : rawUseCases.slice(0, DEFAULT_VISIBLE_COUNT);

    useEffect(() => {
        const handleScroll = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const containerTop = rect.top;
                const containerHeight = rect.height;
                const windowHeight = window.innerHeight;

                const scrolled = Math.max(0, windowHeight - containerTop);
                const progress = Math.min(1, scrolled / (containerHeight + windowHeight));

                setScrollProgress(progress);
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className={`h-full ${className}`} dir={isRTL ? "rtl" : "ltr"}>
            <section className="relative overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <SectionHeader
                        className="mb-8"
                        regularText={finalSectionTitle.regularText}
                        highlightedText={finalSectionTitle.highlightedText}
                        descriptionHighlight={finalSectionTitle.descriptionHighlight}
                        descriptionText={finalSectionTitle.descriptionText}
                    />

                    {/* Filter tabs */}
                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                        <button
                            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                                activeTab === "main"
                                    ? "bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white shadow-md shadow-blue-500/20"
                                    : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200/80 shadow-2xs"
                            }`}
                            onClick={() => {
                                setActiveTab("main");
                                setShowAllCards(false);
                            }}
                        >
                            <Sparkles className="w-4 h-4" />
                            {t("tabs.allDay")}
                        </button>

                        {usedData.categories.map((category) => (
                            <button
                                key={category.id}
                                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer ${
                                    activeTab === category.id
                                        ? "bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white shadow-md shadow-blue-500/20"
                                        : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200/80 shadow-2xs"
                                }`}
                                onClick={() => {
                                    setActiveTab(category.id);
                                    setShowAllCards(false);
                                }}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <main ref={containerRef} className="max-w-4xl mx-auto relative pb-10">
                <div className="transition-opacity duration-300">
                    {filteredUseCases.map((useCase, i) => (
                        <UseCase key={`${activeTab}-${i}`} {...useCase} index={i} scrollProgress={scrollProgress} totalCards={filteredUseCases.length} />
                    ))}
                </div>

                {hasMore && (
                    <div className="flex justify-center mt-8 relative z-30">
                        <button
                            onClick={() => setShowAllCards(!showAllCards)}
                            className="px-6 py-3 rounded-xl bg-white hover:bg-blue-50/70 text-[#0038A8] border border-blue-200/90 font-bold text-sm transition-all duration-200 flex items-center gap-2.5 shadow-md hover:shadow-lg cursor-pointer"
                        >
                            <span>{showAllCards ? t("buttons.showLess") : t("buttons.showMore")}</span>
                            <ChevronDown
                                className={`w-4 h-4 transform transition-transform duration-300 ${showAllCards ? "rotate-180" : "rotate-0"}`}
                            />
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
};

export default LifestyleUseCases;
